import { format } from "date-fns";
import { enUS, zhTW } from "date-fns/locale";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { components } from "../../../apis/gen";
import { BlockButton } from "../../../inJoy-components/Button/BlockButton";
import { IconButton } from "../../../inJoy-components/Button/IconButton";
import { Icon } from "../../../inJoy-components/Icon/Icon";
import type { EventStatus } from "../types";
import { GameCard } from "./GameCard";

type Event = components["schemas"]["Event"];

interface EventCardProps {
	event: Event;
	status?: EventStatus;
	onQuickGame?: (event: Event) => Promise<void>;
	isAddingGame?: boolean;
}

export const EventCard = ({
	event,
	status = "upcoming",
	onQuickGame,
	isAddingGame = false,
}: EventCardProps) => {
	const { t, i18n } = useTranslation();
	const dateFnsLocale = i18n.language === "zh" ? zhTW : enUS;
	const eventDate = new Date(event.started_at);
	const dateStr = format(eventDate, "yyyy-MM-dd");
	const dayStr = format(eventDate, "EEE", { locale: dateFnsLocale });
	const timeStr = format(eventDate, "h:mm aa");

	const [isDebug] = useState(() => localStorage.getItem("debug") === "true");

	const getStatusClassName = (): string => {
		switch (status) {
			case "upcoming":
				return "text-color_border_grey_5";
			case "past":
				return "text-color_text_positive_4";
			case "cancelled":
				return "text-color_border_grey_5";
			default:
				return "text-color_border_grey_5";
		}
	};

	const getStatusText = (): string => {
		switch (status) {
			case "upcoming":
				return t("events.status.scheduled");
			case "past":
				return t("events.status.complete");
			case "cancelled":
				return t("events.status.cancelled");
			default:
				return t("events.status.unknown");
		}
	};

	const [isSessionShowed, setIsSessionShowed] = useState(false);

	return (
		<div
			key={event.event_id}
			className="rounded-lg hover:shadow-lg transition-shadow bg-white"
		>
			<div className="flex flex-col">
				<Link
					to={`/events/${event.event_id}`}
					className="p-5 flex items-center"
				>
					{/* Date/Time */}
					<div
						className={`flex flex-col items-center justify-start min-w-[150px] ${status === "cancelled" ? "opacity-40" : ""}`}
					>
						<p className="text-sm font-medium text-color-Grey900">
							{dateStr} &bull; {dayStr}
						</p>
						<p className="mt-0 mb-2 text-xl font-semibold text-color-Grey900">
							{timeStr}
						</p>
					</div>

					{/* Event Info */}
					<div
						className={`flex-1 self-stretch border-l border-color-Grey200 pl-5 ${status === "cancelled" ? "opacity-40" : ""}`}
					>
						<p className="text-xl font-medium">{event.name}</p>
						<div className="mt-3 flex items-center gap-x-1">
							<Icon
								iconName="user_fill"
								size="Large"
								color="#B1B1B1"
								loading={false}
								onClick={() => undefined}
							/>
							<span className="text-base text-color-Grey500">
								{event.booker || t("events.unknown_user")}
							</span>
							<span className="mx-1 px-1 text-base text-color-Grey500">
								{" "}
								&bull;{" "}
							</span>
							<Icon
								iconName="location_outline"
								size="Large"
								color="#B1B1B1"
								loading={false}
								onClick={() => undefined}
							/>
							<span className="text-base text-color-Grey500">
								{event.location || t("events.no_location")}
							</span>
						</div>
						<p className={`mt-3 text-base ${getStatusClassName()}`}>
							{getStatusText()}
						</p>
					</div>
					{/* Action Buttons */}
					<div className="flex flex-col items-center">
						<BlockButton
							type="Secondary"
							size="Large"
							icon=""
							disabled={false}
							loading={false}
							loadingIcon=""
							onClick={() => undefined}
							width="200px"
						>
							{t("events.share_event")}
						</BlockButton>
					</div>
				</Link>
				{/* Sessions */}
				{status !== "cancelled" && (
					<div className="py-3 px-5 border-t border-color-Grey200">
						<div
							className="flex items-center gap-x-2 cursor-pointer"
							onClick={() => {
								setIsSessionShowed(!isSessionShowed);
							}}
						>
							<p className="text-base font-medium text-color-Grey900">
								{t("events.view_game_details")}
							</p>
							<span className="flex-1 text-base font-medium text-color-Grey500">
								{t("events.games_count", {
									count: event.sessions?.length || 0,
								})}
							</span>
							{status === "upcoming" && onQuickGame && isDebug && (
								<div onClick={(e) => e.stopPropagation()}>
									<BlockButton
										type="Secondary"
										size="Small"
										icon="plus_outline"
										loadingIcon="spinner_outline"
										loading={isAddingGame}
										disabled={isAddingGame}
										onClick={() => onQuickGame(event)}
										width="140px"
									>
										{t("events.quick_game")}
									</BlockButton>
								</div>
							)}
							<IconButton
								icon={isSessionShowed ? "arrow_up" : "arrow_down"}
								type="Tertiary"
								size="Small"
								disabled={false}
							/>
						</div>
						{/* Sessions */}
						{event.sessions &&
							event.sessions.length === 0 &&
							isSessionShowed && (
								<div className="mt-3 py-5 text-center">
									<p className="text-base text-color-Grey900">
										{t("events.no_games_yet")}
									</p>
								</div>
							)}
						{event.sessions && event.sessions.length > 0 && isSessionShowed && (
							<div className="mt-3 grid grid-cols-2 gap-3">
								{event.sessions.map((session, idx) => (
									<GameCard
										key={idx}
										session={session}
										idx={idx}
										status={status}
										eventId={event.event_id}
									/>
								))}
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
};
